/**
 * Offres groupées : paliers de quantité et couleur par exemplaire.
 *
 * Synchronisation avec le reste de la fiche produit : ce bloc ne connaît aucun autre
 * bloc. Il parle uniquement au sélecteur de variantes natif et écoute variant:updated.
 * C'est ce qui rend toute boucle impossible, par construction :
 *
 *   - un clic sur la LIGNE #1 remonte au sélecteur natif → le thème émet variant:updated
 *     → on repose la ligne #1 de chaque palier sur cette couleur ;
 *   - le chemin descendant n'écrit que des classes, il ne redéclenche jamais rien.
 *
 * Les lignes #2 et suivantes restent strictement locales : c'est tout l'intérêt du lot
 * mélangé. Sans sélecteur natif sur la page, tout ce mécanisme se met en sommeil et le
 * bloc se comporte exactement comme avant.
 *
 * La logique de pilotage du sélecteur natif est volontairement recopiée depuis
 * gb-selecteur-couleur.js plutôt que partagée : les deux blocs doivent rester utilisables
 * l'un sans l'autre, sans dépendance ni ordre de chargement entre eux.
 */

const ACTIVE = 'gb-bundle-item__pastille--active';

/** Normalise une valeur d'option pour comparer « Rosé doré » et « rose dore ». */
const normaliser = (valeur) =>
  (valeur == null ? '' : String(valeur))
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const memeValeur = (a, b) => a === b || normaliser(a) === normaliser(b);

/**
 * Écrit un prix et son prix barré dans un conteneur du thème.
 *
 * textContent, jamais innerHTML : la valeur peut venir d'un champ libre saisi dans
 * l'éditeur, on ne l'interprète pas comme du HTML.
 *
 * Le span du prix barré est TOUJOURS rendu par snippets/price.liquid:78, même vide :
 * une chaîne vide l'efface proprement : il n'y a pas de cas où la cible manque.
 *
 * Le badge de réduction, lui, est vidé : calculé sur la variante, il annoncerait un
 * pourcentage sans rapport avec le palier. Mieux vaut pas de badge qu'un faux.
 */
function ecrirePrix(conteneur, prix, barre) {
  const champPrix = conteneur.querySelector('[data-ref="price"]');
  if (champPrix) champPrix.textContent = prix;

  const champBarre = conteneur.querySelector('[data-ref="compare-at-price"]');
  if (champBarre) champBarre.textContent = barre;

  const badge = conteneur.querySelector('[data-ref="difference-price-value"]');
  if (badge) badge.textContent = '';
}

class GbBundle extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-ref="gb-bundle-item"]'));
    if (!this.items.length) return;

    this.section = this.closest('.shopify-section');

    // Nom du champ natif et rang de l'option, posés par gb-bundle-item.liquid.
    const reference = this.items.find((item) => item.dataset.nomChamp);
    this.nomChamp = reference ? reference.dataset.nomChamp : '';
    this.indexOption = reference ? Number.parseInt(reference.dataset.indexOption, 10) : Number.NaN;

    this.items.forEach((item) => {
      item.addEventListener('click', (e) => {
        if (e.target.closest('[data-ref="gb-bundle-pastille"]')) return;
        this.selectionner(item);
      });

      item.querySelectorAll('[data-ref="gb-bundle-ligne"]').forEach((ligne) => {
        const estLigne1 = ligne.dataset.index === '1';

        ligne.querySelectorAll('[data-ref="gb-bundle-pastille"]').forEach((pastille) => {
          pastille.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.activer(ligne, pastille);
            // Seule la ligne #1 remonte. Les autres ne sortent jamais de leur ligne.
            if (estLigne1) this.transmettre(pastille.dataset.valeur);
          });
        });

        const premiere = ligne.querySelector('[data-ref="gb-bundle-pastille"]');
        if (premiere) this.activer(ligne, premiere);
      });
    });

    // La ligne #1 de chaque palier part sur la couleur réellement sélectionnée,
    // pas sur la première pastille. Les autres lignes gardent la première.
    const couleurActuelle = this.lireValeurNative();
    if (couleurActuelle) this.synchroniserLignes1(couleurActuelle);

    if (this.section) {
      this.surVariante = this.surVariante.bind(this);
      this.section.addEventListener('variant:updated', this.surVariante);

      this.surFormulaire = this.surFormulaire.bind(this);
      this.section.addEventListener('form:updated', this.surFormulaire);
    }

    const index = parseInt(this.dataset.palierDefaut || '1', 10) - 1;
    this.selectionner(this.items[index] || this.items[0]);

    this.brancherFormulaire();
  }

  disconnectedCallback() {
    if (this.section && this.surFormulaire) {
      this.section.removeEventListener('form:updated', this.surFormulaire);
    }
    if (this.section && this.surVariante) {
      this.section.removeEventListener('variant:updated', this.surVariante);
    }
    if (this.surSoumission) {
      document.removeEventListener('submit', this.surSoumission, true);
    }
  }

  selectionner(item) {
    this.items.forEach((i) => i.classList.remove('gb-bundle-item--selectionne'));
    item.classList.add('gb-bundle-item--selectionne');
    this.itemActif = item;
    this.appliquerPrix();
  }

  /** N'écrit que de l'état visuel : aucun effet de bord, donc aucune cascade possible. */
  activer(ligne, pastille) {
    ligne.querySelectorAll('[data-ref="gb-bundle-pastille"]').forEach((p) => p.classList.remove(ACTIVE));
    pastille.classList.add(ACTIVE);
  }

  // ---------------------------------------------------------------------------
  // Dialogue avec le sélecteur de variantes natif
  // ---------------------------------------------------------------------------

  /** Champs du sélecteur natif portant notre option. Re-cherchés à chaque appel :
   *  le thème remplace l'intérieur de <variant-picker> après chaque mise à jour. */
  champsNatifs() {
    if (!this.section || !this.nomChamp) return [];
    const selecteur = this.section.querySelector('variant-picker');
    if (!selecteur) return [];
    return Array.from(selecteur.querySelectorAll('input[type="radio"], select')).filter(
      (champ) => champ.name === this.nomChamp
    );
  }

  lireValeurNative() {
    for (const champ of this.champsNatifs()) {
      if (champ instanceof HTMLSelectElement) return champ.value;
      if (champ.checked) return champ.value;
    }
    return '';
  }

  /** Répercute la couleur de la ligne #1 sur le sélecteur natif. Sans sélecteur, ne fait rien. */
  transmettre(valeur) {
    if (!valeur) return false;

    const champs = this.champsNatifs();
    if (champs.length === 0) return false; // pas de sélecteur natif : le bloc vit sa vie

    for (const champ of champs) {
      if (champ instanceof HTMLSelectElement) {
        const option = Array.from(champ.options).find((o) => memeValeur(o.value, valeur));
        if (!option) continue;
        if (champ.value === option.value) return true;
        champ.value = option.value;
        champ.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      if (memeValeur(champ.value, valeur)) {
        if (champ.checked) return true; // déjà coché, rien à provoquer
        champ.checked = true;
        champ.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }

    return false;
  }

  /** Le thème a changé de variante. Chemin descendant : on n'écrit que des classes. */
  surVariante(evenement) {
    const variante = evenement.detail && evenement.detail.resource;
    if (!variante) return;

    const origine = evenement.target;
    if (!(origine instanceof Node) || !this.section.contains(origine)) return;

    // La variante affichée a changé : les paliers doivent l'ajouter, elle et pas une autre.
    if (variante.id) {
      this.items.forEach((item) => {
        item.dataset.variantId = String(variante.id);
      });
    }

    if (!Number.isInteger(this.indexOption) || this.indexOption < 0) return;
    const valeur = (variante.options || [])[this.indexOption];
    if (valeur == null) return;

    this.synchroniserLignes1(valeur);
  }

  /** Pose la ligne #1 de CHAQUE palier sur cette couleur, affiché ou non.
   *  C'est ce qui fait qu'au changement de palier, la ligne #1 est déjà bonne. */
  synchroniserLignes1(valeur) {
    this.items.forEach((item) => {
      const ligne = item.querySelector('[data-ref="gb-bundle-ligne"][data-index="1"]');
      if (!ligne) return;

      const pastille = Array.from(ligne.querySelectorAll('[data-ref="gb-bundle-pastille"]')).find((p) =>
        memeValeur(p.dataset.valeur, valeur)
      );
      if (pastille) this.activer(ligne, pastille);
    });
  }

  // ---------------------------------------------------------------------------
  // Prix affiché ailleurs dans la fiche
  // ---------------------------------------------------------------------------

  /**
   * Le thème vient de réécrire les prix du formulaire : product-form.js:103 diffuse
   * form:updated juste après les avoir posés. On repasse derrière lui.
   *
   * L'ordre n'a pas besoin d'être forcé, il est déterministe : product-price.js écoute
   * sur l'élément <product-form> lui-même (product-price.js:15), donc en phase « at
   * target » ; nous écoutons sur la section, un ancêtre, donc en phase de bulle. Un
   * ancêtre est toujours servi après la cible.
   */
  surFormulaire() {
    this.appliquerPrix();
  }

  /**
   * Fait dire au bouton d'ajout au panier et au bloc Prix du produit ce que dit la
   * carte du palier sélectionné.
   *
   * Pourquoi on écrit la CHAÎNE et pas des centimes : le prix d'une offre peut être
   * saisi librement (« 40€ »). Passer par data-price ferait reformater le thème en
   * « 40,00 € », et la carte et le bouton ne diraient plus la même chose. La carte est
   * la source, au caractère près — c'est tout l'intérêt du champ libre.
   *
   * Le thème ne sait pas faire ce calcul pour nous : product-form.js:338 ne consulte
   * les paliers que via this.querySelector('[data-ref="quantity-breaks"]'), son propre
   * bloc, cherché À L'INTÉRIEUR du formulaire. gb-bundle ne peut pas y vivre, la liste
   * d'enfants du Formulaire produit étant fermée.
   */
  appliquerPrix() {
    if (!this.itemActif) return;

    const prix = this.itemActif.dataset.prixAffiche || '';
    if (!prix) return;

    const barre = this.itemActif.dataset.prixBarre || '';
    const perimetre = this.section || document;
    const cibles = [];

    const bouton = perimetre.querySelector('[data-ref="add-to-cart-button-container"]');
    if (bouton) cibles.push(bouton);

    // Le bloc Prix du produit, à l'exclusion de ceux des cartes produit (cross-sells,
    // suggestions), que product-price.js traite dans leur propre périmètre.
    perimetre.querySelectorAll('product-price [data-ref="price-container"]').forEach((conteneur) => {
      if (!conteneur.closest('product-card')) cibles.push(conteneur);
    });

    cibles.forEach((cible) => ecrirePrix(cible, prix, barre));
  }

  // ---------------------------------------------------------------------------
  // Panier
  // ---------------------------------------------------------------------------

  construireArticles() {
    const articles = [];
    if (!this.itemActif) return articles;

    const lignes = this.itemActif.querySelectorAll('[data-ref="gb-bundle-ligne"]');

    if (this.dataset.pastilles === 'true' && lignes.length) {
      lignes.forEach((ligne) => {
        const active = ligne.querySelector('.' + ACTIVE);
        if (active) {
          articles.push({ id: parseInt(active.dataset.variantId, 10), quantity: 1 });
        }
      });
    } else {
      articles.push({
        id: parseInt(this.itemActif.dataset.variantId, 10),
        quantity: parseInt(this.itemActif.dataset.quantite, 10),
      });
    }

    return articles;
  }

  /**
   * Le bloc ne peut PAS vivre dans le formulaire produit : la liste de blocs enfants de
   * _product-form.liquid est fermée et n'accepte pas @theme. this.closest('form…')
   * renvoyait donc toujours null, l'interception n'était jamais branchée, et le thème
   * ajoutait la seule variante sélectionnée. On cherche le formulaire dans la section.
   */
  brancherFormulaire() {
    const perimetre = this.section || document;
    this.formulaire =
      this.closest('form[action*="/cart/add"]') || perimetre.querySelector('form[action*="/cart/add"]');

    if (!this.formulaire) {
      console.warn('[gb-bundle] formulaire produit introuvable : ajout au panier laissé au thème');
      return;
    }

    // Capture sur document : on passe forcément avant l'écouteur que le thème pose sur
    // le formulaire (product-form.js:34), sans dépendre de l'ordre de chargement des
    // scripts. stopPropagation empêche ensuite son gestionnaire de s'exécuter.
    this.surSoumission = this.surSoumission.bind(this);
    document.addEventListener('submit', this.surSoumission, true);
  }

  /**
   * Reproduit le chemin d'ajout au panier du thème, à l'identique :
   *   product-form.js:240  POST vers Theme.routes.cart_add_url, avec les sections
   *                        'auto-gifts' et celle du tiroir de panier ;
   *   product-form.js:264  un seul événement cart:updated sur document, qui pilote tout ;
   *   cart-drawer.js:69    re-rend le contenu du tiroir depuis data.sections ;
   *   cart-icon.js:31      met à jour le compteur et OUVRE le tiroir, sauf si le
   *                        réglage du thème demande une notification.
   * Aucune redirection : on reste sur la fiche produit, comme le thème.
   */
  async surSoumission(evenement) {
    if (evenement.target !== this.formulaire) return;

    const articles = this.construireArticles();
    if (!articles.length) return;

    evenement.preventDefault();
    evenement.stopPropagation();

    const bouton = this.formulaire.querySelector('[data-ref="add-to-cart-button"]');
    if (bouton) bouton.classList.add('is-loading');

    const tiroir = document.querySelector('cart-drawer');
    const sections = ['auto-gifts'];
    if (tiroir && tiroir.dataset.sectionId) sections.push(tiroir.dataset.sectionId);

    const quantiteAjoutee = articles.reduce((total, a) => total + (parseInt(a.quantity, 10) || 1), 0);

    console.log(
      '[gb-bundle] envoi au panier —',
      `palier ${this.itemActif?.dataset.quantite}`,
      `| ${articles.length} article(s)`,
      `| quantité totale ${quantiteAjoutee}`
    );
    console.table(articles);

    try {
      const reponse = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: articles, sections: sections.join(',') }),
      });

      const resultat = await reponse.json().catch(() => null);
      if (!reponse.ok) {
        throw new Error((resultat && (resultat.description || resultat.message)) || `Ajout refusé (${reponse.status})`);
      }

      // Shopify fusionne les articles de même variante : trois lignes de la même
      // couleur donnent UNE ligne de quantité 3, trois couleurs donnent trois lignes.
      if (resultat && Array.isArray(resultat.items)) {
        console.log('[gb-bundle] lignes créées par Shopify :', resultat.items.length);
        console.table(resultat.items.map((i) => ({ id: i.id, titre: i.title, quantite: i.quantity })));
      } else {
        console.log('[gb-bundle] réponse sans détail des lignes', resultat);
      }

      const icone = document.querySelector('cart-icon');
      const compteActuel = icone && icone.getCurrentItemCount ? icone.getCurrentItemCount() : null;
      const nouveauCompte =
        typeof compteActuel === 'number' && !Number.isNaN(compteActuel) ? compteActuel + quantiteAjoutee : undefined;

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            resource: {},
            data: {
              itemCount: nouveauCompte,
              source: 'product-form',
              sections: resultat ? resultat.sections : undefined,
            },
          },
        })
      );

      if (bouton) bouton.classList.remove('is-loading');
      document.dispatchEvent(new CustomEvent('gb-bundle:ajoute', { detail: { articles } }));

      if (icone && icone.addToCartBehavior === 'notification') {
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: { type: 'success', message: window.Theme?.translations?.addToCartNotificationMessage },
          })
        );
      }
    } catch (erreur) {
      console.error('[gb-bundle] ajout au panier échoué :', erreur);
      if (bouton) bouton.classList.remove('is-loading');
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: erreur.message } }));
    }
  }
}

if (!customElements.get('gb-bundle')) {
  customElements.define('gb-bundle', GbBundle);
}
